import { Gauge } from "@mui/x-charts";
import React, { useEffect, useState } from "react";
import { useBeforeUnload, useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExamResults from "../../components/ResultExam";
import { getUserExam, getUserQuestion, postExamQuestion } from "../../services/Exam";
import { ExamContainer, ExamResultContainer } from "../../style/User/Exam";
import { Loader } from "../../utils/loader";
import { useAntiCheat } from "../../utils/useAntiCheat";

function Exam() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const courseId = state?.courseId || null;
  const moduleId = state?.moduleId || null;

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [startExam, setStartExam] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [examInfo, setExamInfo] = useState({ examTitle: "", passingMarks: 0, timeLimit: 0, examDescription: "", questionCount: 0, examId: null , courseId: null });
  const [score, setScore] = useState({ passed: false, percentage: 0, message: "", success: false });

  const MAX_WARNINGS = 3;

  const { warningCount, isFullscreen, displayWarning, enableFullscreen, exitFullscreen, resetAntiCheat } = useAntiCheat({
    active: startExam && !isSubmitted,
    isSubmitted,
    maxWarnings: MAX_WARNINGS,
    onMaxWarningsReached: handleSubmit,
  });

  useBeforeUnload((event) => {
    if (startExam && !isSubmitted) {
      event.preventDefault();
      return (event.returnValue = "Changes you made may not be saved.");
    }
  });

  const formatTime = (seconds) => `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  useEffect(() => {
    const fetchExamData = async () => {
      try {
        const response = await getUserExam(moduleId);
        if (response.success) {
          const { exam } = response;

          setTimeRemaining(exam.timeLimit * 60);
          setExamInfo({
            examId: exam.id,
            examTitle: exam.examTitle,
            timeLimit: exam.timeLimit,
            passingMarks: exam.passingMarks,
            examDescription: exam.examDescription,
            questionCount: exam.questionCount,
            courseId: courseId,
          });
        }
      } catch (error) {
        console.error("Error fetching exam data:", error);
        toast.error("Failed to load exam data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchExamData();
  }, [moduleId, courseId]);

  useEffect(() => {
    let timer;
    if (timerActive && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
    } else if (timerActive && timeRemaining === 0) {
      handleSubmit();
    }

    return () => timer && clearInterval(timer);
  }, [timerActive, timeRemaining]);

  const handleStartExam = async () => {
    try {
      enableFullscreen();
      const response = await getUserQuestion(examInfo.examId);
      const { questions } = response;
      const formattedQuestions = questions.map((q) => ({
        id: q.id,
        question: q.questionTitle,
        options: Object.entries(q.options).map(([key, value]) => ({
          label: key,
          text: value,
        })),
      }));

      setQuestions(formattedQuestions);
      setSelectedAnswers(Array(formattedQuestions.length).fill(""));
    } catch (error) {
      console.error("Failed to enter fullscreen:", error);
      setStartExam(true);
      setTimerActive(true);
      toast.warning("Unable to enter fullscreen. The exam will continue, but fullscreen is recommended.");
    } finally {
      setStartExam(true);
      setTimerActive(true);
      toast.info("Exam started. Good luck!");
    }
  };

  const handleOptionSelect = (optionLabel) => {
    setSelectedAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[currentQuestion] = optionLabel;
      return newAnswers;
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleQuestionSelect = (index) => {
    setCurrentQuestion(index);
  };

  function handleSubmit() {
    setTimerActive(false);
    setIsSubmitted(true);
    exitFullscreen();

    const payload = {
      examId: examInfo.examId,
      courseId,
      moduleId,
      answers: questions
        .map((question, index) => ({
          questionId: question.id,
          selectedOption: selectedAnswers[index] || null,
        }))
        .filter((answer) => answer.selectedOption !== null),
    };

    postExamQuestion(payload)
      .then((response) => {
        setScore({
          passed: response.passed,
          percentage: response.percentage.toFixed(2),
          message: response.message || response.massage || "",
          success: response.success,
        });

        if (response.passed) {
          toast.success("Congratulations! You've passed the exam.");
        } else {
          toast.error("You didn't pass this time. Review your answers and try again.");
        }
      })
      .catch((error) => {
        console.error("Error submitting exam:", error);
        toast.error("Failed to submit exam. Please try again.");
      });
  }

  const handleCancelExam = () => {
    exitFullscreen();
    navigate(-1);
  };

  const handleTryAgain = () => {
    setIsSubmitted(false);
    setCurrentQuestion(0);
    setSelectedAnswers(Array(questions.length).fill(""));
    setStartExam(false);
    setTimeRemaining(examInfo.timeLimit * 60);
    setTimerActive(false);
    resetAntiCheat();
    toast.info("Starting a new attempt. Good luck!");
  };

  if (loading) return <Loader fullScreen />;

  const timerConfig = {
    value: (timeRemaining / (examInfo.timeLimit * 60)) * 100,
    text: formatTime(timeRemaining),
    max: 100,
  };

  const renderExamIntro = () => (
    <div className="confirmation-screen">
      <div className="confirm-flex">
        <h2>{examInfo.examTitle}</h2>
        <div className="exam-details">
          <p>
            <strong>Number of Questions:</strong> {examInfo.questionCount}
          </p>
          <p>
            <strong>Time Limit:</strong> {examInfo.timeLimit} minutes
          </p>
          <p>
            <strong>Passing Score:</strong> {examInfo.passingMarks}%
          </p>
          <p>
            <strong>Description:</strong> {examInfo.examDescription}
          </p>
        </div>
        <div className="exam-instructions">
          <h3>Instructions:</h3>
          <ul>
            <li>Read questions carefully before answering.</li>
            <li>Use Previous/Next buttons or question numbers to navigate.</li>
            <li>Once submitted, answers cannot be changed.</li>
            <li>Submission is allowed even with unanswered questions.</li>
            <li>Exam auto-submits when the time limit is reached.</li>
            <li>Fullscreen mode is required; stay focused on this tab.</li>
            <li>Leaving the exam, tab switching, or using shortcuts triggers warnings.</li>
            <li>After {MAX_WARNINGS} warnings, the exam auto-submits.</li>
          </ul>
        </div>
        <div className="confirmation-buttons">
          <button className="button" onClick={handleStartExam}>
            Start Exam
          </button>
          <button className="button" onClick={handleCancelExam}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  const renderQuestion = () => {
    const currentQ = questions[currentQuestion];
    return (
      <>
        <div className="question-header">
          <div className="flex-between">
            <h2>
              Question {currentQuestion + 1} of {questions.length}
            </h2>
            <div className="timer-display">
              <p>
                Time: <span className={timeRemaining < 60 ? "timer-warning" : ""}>{formatTime(timeRemaining)}</span>
              </p>
            </div>
          </div>
          <p>{currentQ?.question}</p>
        </div>
        <div className="options-container">
          {currentQ?.options.map((option, index) => (
            <div className="option" key={index} onClick={() => handleOptionSelect(option.label)}>
              <label className="option-label">
                <input
                  type="radio"
                  name={`question-${currentQuestion}`}
                  checked={selectedAnswers[currentQuestion] === option.label}
                  onChange={() => handleOptionSelect(option.label)}
                />
                <span className="option-text">{`${option.label}) ${option.text}`}</span>
              </label>
            </div>
          ))}
        </div>
        <div className="navigation-buttons">
          <button onClick={handlePrev} disabled={currentQuestion === 0} className="nav-button">
            Previous
          </button>
          {currentQuestion !== questions.length - 1 ? (
            <button onClick={handleNext} className="nav-button">
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="nav-button"
              // disabled={selectedAnswers.every((answer) => answer === "")}
            >
              Submit Exam
            </button>
          )}
        </div>
      </>
    );
  };

  const renderQuestionList = () => (
    <div className="question-list">
      <h3>{examInfo.examTitle}</h3>
      <div className="timer">
        <Gauge className="gauge_timer" {...timerConfig} />
      </div>
      <div className="question-grid">
        {questions.map((_, index) => (
          <div
            key={index}
            className={`question-number ${currentQuestion === index ? "active" : ""} ${selectedAnswers[index] ? "answered" : ""}`}
            onClick={() => handleQuestionSelect(index)}
          >
            {index + 1}
          </div>
        ))}
      </div>
      {warningCount > 0 && (
        <div className="warning-counter">
          <p>
            Warnings: {warningCount}/{MAX_WARNINGS}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <ToastContainer />
      <ExamContainer>
        <div className="question-container">
          {!startExam ? (
            renderExamIntro()
          ) : !isSubmitted ? (
            renderQuestion()
          ) : (
            <ExamResultContainer>
              <ExamResults score={score} examInfo={examInfo} timeRemaining={timeRemaining} formatTime={formatTime} handleTryAgain={handleTryAgain} />
            </ExamResultContainer>
          )}
        </div>
        {!isSubmitted && startExam && renderQuestionList()}
      </ExamContainer>
    </>
  );
}

export default Exam;
